/**
 * Choices on the transcription setup form. Values are what the API and the
 * AI Content tab expect (raw English names); show them through t().
 * The note-taker keeps its own list with the same values.
 */
export const TRANSCRIPTION_AUDIO_TYPES = [
  'Meeting',
  'Podcast',
  'Lecture',
  'Sales Call',
  'Coaching Session',
  'Phone Call',
  'Therapy Session',
  'Webinar',
  'Other',
] as const;

/**
 * Spoken languages offered on the setup form, as display names: the API
 * resolves them to AssemblyAI codes (src/lib/assemblyLanguage.ts). Leaving
 * the choice empty means auto-detect.
 */
export const TRANSCRIPTION_LANGUAGES = [
  'English',
  'Albanian',
  'Arabic',
  'Bengali',
  'Bulgarian',
  'Chinese',
  'Croatian',
  'Czech',
  'Danish',
  'Dutch',
  'Estonian',
  'Filipino',
  'Finnish',
  'French',
  'German',
  'Greek',
  'Hebrew',
  'Hindi',
  'Hungarian',
  'Icelandic',
  'Indonesian',
  'Italian',
  'Japanese',
  'Kazakh',
  'Korean',
  'Latvian',
  'Lithuanian',
  'Macedonian',
  'Malay',
  'Norwegian',
  'Persian',
  'Polish',
  'Portuguese',
  'Romanian',
  'Russian',
  'Serbian',
  'Slovak',
  'Slovenian',
  'Spanish',
  'Swedish',
  'Tamil',
  'Thai',
  'Turkish',
  'Ukrainian',
  'Vietnamese',
] as const;

/** Sources a transcription can come from (audios.type for these flows). */
export const TRANSCRIPTION_LINK_SOURCES = [
  'youtube',
  'instagram',
  'tiktok',
  'googledrive',
  'importurl',
] as const;

export type TranscriptionLinkSource =
  (typeof TRANSCRIPTION_LINK_SOURCES)[number];

export const isLinkSource = (
  type: string | null | undefined,
): type is TranscriptionLinkSource =>
  !!type && (TRANSCRIPTION_LINK_SOURCES as readonly string[]).includes(type);

/**
 * Recordings: the old in-page recorder stores type 'mic', the note-taker
 * stores the recording's audio type ('Meeting', 'Podcast'…) as the type.
 */
export const isRecordingSource = (type: string | null | undefined) =>
  type === 'mic' ||
  (!!type && (TRANSCRIPTION_AUDIO_TYPES as readonly string[]).includes(type));

/** What the file pickers accept: every format AssemblyAI takes. */
export const TRANSCRIPTION_FILE_ACCEPT =
  '.3ga,.8svx,.aac,.ac3,.aif,.aiff,.alac,.amr,.ape,.au,.dss,.flac,.flv,.m4a,.m4b,.m4p,.m4r,.mp3,.mpga,.ogg,.oga,.mogg,.opus,.qcp,.tta,.voc,.wav,.wma,.wv,.webm,.mts,.m2ts,.ts,.mov,.mp2,.mp4,.m4v,.mxf,audio/*,video/*';

/**
 * Model of a transcript's "Chat with Transcript" tab. It runs on every plan,
 * Free included, although it is paid-only in Marky's model picker:
 * /api/new-chat lets it through for a chat that belongs to the user's own
 * transcript (see isOwnTranscriptChat in @/data/audios).
 */
export const TRANSCRIPT_CHAT_MODEL = 'claude-haiku-5-5';

/** Tabs of a transcript's page (also the public share page). */
export enum TABS {
  /** The transcript with speakers and timestamps (speaker detection on). */
  TRANSCRIPTION = 'Transcription',
  /** The plain transcript (speaker detection off). */
  OUTPUTS = 'Plain Text',
  ADDITIONAL = 'AI Content',
  CHAT = 'Chat with Transcript',
}
